import { Directive, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { CardType, OrganizationPaymentAccount, PaymentAccount, State, StateService } from '@uzurv/clara';
import { CreditCard, CreditCardValidators } from 'angular-cc-library';
import moment from 'moment-timezone';
import { LegacyFeatureFlagService } from '../../../services/legacy-feature-flag/legacy-feature-flag.service';
import { map } from 'rxjs';

export interface AbstractPaymentAccountFormData {
  paymentAccount: PaymentAccount;
  isEditMode: boolean;
}

@Directive()
export abstract class AbstractPaymentAccountForm implements OnInit {
  protected readonly fb = inject(FormBuilder);
  protected readonly domSanitizer = inject(DomSanitizer);
  protected readonly stateService = inject(StateService);
  protected readonly featureFlagService = inject(LegacyFeatureFlagService);

  @Input() paymentAccount: PaymentAccount;
  @Input() cardTypes: Array<CardType>;
  @Input() isEditMode = false;
  @Input() submittingPaymentAccount = false;

  @Output() cancel: EventEmitter<any> = new EventEmitter();
  @Output() delete: EventEmitter<any> = new EventEmitter();
  @Output() create: EventEmitter<PaymentAccount> = new EventEmitter();
  @Output() update: EventEmitter<PaymentAccount> = new EventEmitter();

  creditCardForm: FormGroup<{
    userDescription: FormControl<string>;
    billingFirstName: FormControl<string>;
    billingLastName: FormControl<string>;
    billingStreetAddress1: FormControl<string>;
    billingStreetAddress2: FormControl<string>;
    billingCity: FormControl<string>;
    billingState: FormControl<string>;
    billingZipCode: FormControl<string>;
    cardNumber: FormControl<string>;
    cardExpiration: FormControl<string>;
    cardVerificationValue: FormControl<string>;
  }>;

  states: Array<State> = [];

  cardExpirationMonth: string;
  cardExpirationYear: string;
  detectedCardType = 'credit';
  cardTypeIcon = this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/${this.detectedCardType}.png`);

  isCreditCardValidationEnabled = false;

  ngOnInit(): void {
    this.isCreditCardValidationEnabled = this.featureFlagService.isCreditCardValidationEnabled();

    this.stateService
      .getStates()
      .pipe(
        map(states => {
          this.states = states;
        })
      )
      .subscribe();

    this.creditCardForm = this.fb.group({
      userDescription: [this.paymentAccount.userDescription, [Validators.required, Validators.maxLength(40)]],
      billingFirstName: [this.paymentAccount.billingFirstName, [Validators.required]],
      billingLastName: [this.paymentAccount.billingLastName, [Validators.required]],
      billingStreetAddress1: [this.paymentAccount.billingStreetAddress1],
      billingStreetAddress2: [this.paymentAccount.billingStreetAddress2],
      billingCity: [this.paymentAccount.billingCity],
      billingState: [this.paymentAccount.billingState],
      billingZipCode: [this.paymentAccount.billingZipCode],
      cardNumber: [{ value: '', disabled: this.isEditMode }, [CreditCardValidators.validateCCNumber, Validators.required]],
      cardExpiration: [
        this.paymentAccount.cardExpiration
          ? `${this.paymentAccount.cardExpiration.split('-')[1]}/${this.paymentAccount.cardExpiration.split('-')[0]}`
          : null,
        [CreditCardValidators.validateExpDate, Validators.required]
      ],
      cardVerificationValue: [
        this.paymentAccount.cardVerificationValue,
        [Validators.required, Validators.minLength(3), Validators.maxLength(4)]
      ]
    });

    if (this.isCreditCardValidationEnabled) {
      this.creditCardForm.controls.billingStreetAddress1.addValidators(Validators.required);
      this.creditCardForm.controls.billingCity.addValidators(Validators.required);
      this.creditCardForm.controls.billingState.addValidators(Validators.required);
      this.creditCardForm.controls.billingZipCode.addValidators(Validators.required);
    }

    if (this.isEditMode) {
      this.cardTypeIcon = this.domSanitizer.bypassSecurityTrustResourceUrl(
        `/assets/${this.paymentAccount.cardType.name.replace(' ', '').toLocaleLowerCase()}.png`
      );
    } else {
      this.creditCardForm.controls.cardNumber.valueChanges.subscribe(cardNumber => {
        const cardFromNumber = CreditCard.cardFromNumber(cardNumber);
        this.detectedCardType = cardFromNumber ? cardFromNumber.type : 'credit';
        this.cardTypeIcon = this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/${this.detectedCardType}.png`);
      });
    }

    if (this.isExpired) {
      this.creditCardForm.disable();
    }
  }

  get isUnverified(): boolean {
    if (this.isEditMode && !this.isExpired && this.isCreditCardValidationEnabled && !this.paymentAccount.verified) {
      return true;
    }

    return false;
  }

  get isExpired(): boolean {
    if (this.isEditMode && moment(this.paymentAccount.cardExpiration).isBefore(moment(), 'month')) {
      return true;
    }

    return false;
  }

  deletePaymentAccount(): void {
    this.delete.emit();
  }

  submitPaymentAccount(): void {
    if (this.isEditMode) {
      this.update.emit(Object.assign(this.paymentAccount, this.creditCardForm.value));
    } else {
      this.create.emit(Object.assign(this.paymentAccount, this.creditCardForm.value));
    }
  }

  handleCancel(): void {
    this.cancel.emit();
  }
}
